import { useLayoutEffect } from 'react'

// Publishes a sticky banner's rendered height to the `--banner-height` CSS
// custom property so the rest of the layout can offset around it (globals.css
// applies it as body padding-top; full-viewport pages read it directly).
//
// IMPORTANT (single-owner invariant): only ONE component may ever write
// `--banner-height`. `BannerHost` is that sole owner — it calls this hook once
// for whichever sticky strip is currently visible (env maintenance banner OR a
// dynamic banner, never both). Child presentational strips must NOT call this.
export function useBannerHeight(
  ref: React.RefObject<HTMLElement | null>,
  active: boolean,
  // Identity of the currently rendered strip. When the sticky slot swaps
  // between the maintenance strip and a dynamic strip (both share `ref` and
  // keep `active` true), this changing value forces the observer to detach from
  // the stale node and re-observe the new one.
  slotKey?: string,
): void {
  useLayoutEffect(() => {
    const root = document.documentElement
    if (!active || !ref.current) {
      root.style.removeProperty('--banner-height')
      return
    }
    const el = ref.current
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      root.style.setProperty('--banner-height', `${Math.ceil(entry.contentRect.height)}px`)
    })
    observer.observe(el)
    root.style.setProperty(
      '--banner-height',
      `${Math.ceil(el.getBoundingClientRect().height)}px`,
    )
    return () => {
      observer.disconnect()
      root.style.removeProperty('--banner-height')
    }
  }, [ref, active, slotKey])
}
